import React, { useState, useEffect, useRef } from 'react';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  UserPlus,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Plus,
  RefreshCw,
  Search,
  Eye,
  Building2,
  AlertTriangle,
  CreditCard,
  Receipt,
  IndianRupee,
  Download,
  FileSignature,
  Check,
  ScrollText,
  XCircle,
  Upload,
  Trash2,
  FileImage,
  File,
  Lock,
  KeyRound,
  Link,
  Copy,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerPortal = () => {
  const { partner, logout, getAuthHeader, refreshPartner } = usePartnerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Leads data
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  
  // Commissions data
  const [commissions, setCommissions] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  
  // Payment records
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Services data
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [showSchengenDropdown, setShowSchengenDropdown] = useState(false);
  
  // Lead form
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    destination_country: '',
    schengen_country: '',
    custom_country: '',
    travel_date: '',
    num_applicants: 1,
    message: ''
  });
  const [submittingLead, setSubmittingLead] = useState(false);
  
  // Duplicate lead detection
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // Stage config
  const [stageConfig, setStageConfig] = useState([]);

  // Bank details
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankFormData, setBankFormData] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch: ''
  });
  const [savingBank, setSavingBank] = useState(false);

  // Commission screenshot modal
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  // Partner Agreement
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [agreementContent, setAgreementContent] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [acceptingAgreement, setAcceptingAgreement] = useState(false);
  const agreementContentRef = useRef(null);

  // Partner Documents
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    document_type: '',
    document_name: '',
    file: null
  });

  // Change Password
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Lead Detail & Audit Trail
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [leadAuditTrail, setLeadAuditTrail] = useState([]);
  const [loadingAuditTrail, setLoadingAuditTrail] = useState(false);

  // Public Link State
  const [publicLink, setPublicLink] = useState(null);
  const [publicLinkLoading, setPublicLinkLoading] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'leads') {
      fetchLeads();
    } else if (activeTab === 'commissions') {
      fetchCommissions();
    } else if (activeTab === 'payments') {
      fetchPayments();
    } else if (activeTab === 'profile') {
      fetchDocuments();
    }
    // Always fetch public link regardless of tab (for quick access)
    fetchPublicLink();
  }, [activeTab]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchServices(),
        fetchCountries(),
        fetchStageConfig(),
        fetchAgreementStatus(),
        fetchPublicLink()  // Fetch public link on initial load
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreementStatus = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/agreement/status`, {
        headers: getAuthHeader()
      });
      setAgreementStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch agreement status:', error);
    }
  };

  const fetchAgreementContent = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/agreement`, {
        headers: getAuthHeader()
      });
      setAgreementContent(response.data);
    } catch (error) {
      console.error('Failed to fetch agreement:', error);
    }
  };

  const handleAcceptAgreement = async () => {
    if (!hasScrolledToBottom) {
      toast.error('Please scroll to the bottom of the agreement first');
      return;
    }
    if (!agreementChecked) {
      toast.error('Please check the box to confirm you have read and agree');
      return;
    }
    
    setAcceptingAgreement(true);
    try {
      const response = await axios.post(`${API}/partner-portal/agreement/accept`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Agreement accepted successfully!');
      setShowAgreementModal(false);
      setAgreementStatus({...agreementStatus, accepted: true, acceptance_details: response.data});
      fetchAgreementStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept agreement');
    } finally {
      setAcceptingAgreement(false);
    }
  };

  const handleAgreementScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if scrolled to bottom (with 50px tolerance)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  };

  const downloadAgreementPDF = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/agreement/download`, {
        headers: getAuthHeader()
      });
      
      const { agreement, partner: partnerInfo, acceptance } = response.data;
      
      // Create a printable HTML document
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${agreement.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { text-align: center; margin-bottom: 30px; }
            .content { line-height: 1.6; }
            .acceptance-info { margin-top: 40px; padding-top: 20px; border-top: 2px solid #333; }
            .acceptance-info h3 { margin-bottom: 10px; }
            .info-row { margin: 8px 0; }
            .label { font-weight: bold; display: inline-block; width: 150px; }
          </style>
        </head>
        <body>
          <h1>${agreement.title}</h1>
          <p style="text-align: center; color: #666;">Version: ${agreement.version}</p>
          <div class="content">${agreement.content}</div>
          ${acceptance ? `
            <div class="acceptance-info">
              <h3>Acceptance Record</h3>
              <div class="info-row"><span class="label">Company:</span> ${partnerInfo.company_name}</div>
              <div class="info-row"><span class="label">Contact Person:</span> ${partnerInfo.contact_person}</div>
              <div class="info-row"><span class="label">Email:</span> ${partnerInfo.email}</div>
              <div class="info-row"><span class="label">Accepted At:</span> ${new Date(acceptance.accepted_at).toLocaleString()}</div>
              <div class="info-row"><span class="label">IP Address:</span> ${acceptance.ip_address}</div>
            </div>
          ` : ''}
        </body>
        </html>
      `;
      
      // Open in new window for printing/PDF saving
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      toast.error('Failed to download agreement');
    }
  };

  const fetchDashboard = async () => {
    const response = await axios.get(`${API}/partner-portal/dashboard`, {
      headers: getAuthHeader()
    });
    setDashboardData(response.data);
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const response = await axios.get(`${API}/partner-portal/leads`, {
        headers: getAuthHeader()
      });
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLeadsLoading(false);
    }
  };

  const fetchCommissions = async () => {
    try {
      const [commissionsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/partner-portal/commissions`, { headers: getAuthHeader() }),
        axios.get(`${API}/partner-portal/commissions/summary`, { headers: getAuthHeader() })
      ]);
      setCommissions(commissionsRes.data);
      setCommissionSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch commissions');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/services`, {
        headers: getAuthHeader()
      });
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/countries`, {
        headers: getAuthHeader()
      });
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStageConfig = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/stage-config`, {
        headers: getAuthHeader()
      });
      setStageConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch stage config:', error);
    }
  };

  // Fetch Lead Detail with Audit Trail
  const fetchLeadDetail = async (leadId) => {
    setLoadingAuditTrail(true);
    try {
      const [detailRes, auditRes] = await Promise.all([
        axios.get(`${API}/partner-portal/leads/${leadId}`, { headers: getAuthHeader() }),
        axios.get(`${API}/partner-portal/leads/${leadId}/audit-trail`, { headers: getAuthHeader() })
      ]);
      setSelectedLead(detailRes.data);
      setLeadAuditTrail(auditRes.data.audit_trail || []);
      setShowLeadDetailModal(true);
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
      toast.error('Failed to load lead details');
    } finally {
      setLoadingAuditTrail(false);
    }
  };

  const openLeadDetail = (lead) => {
    fetchLeadDetail(lead.id);
  };

  const closeLeadDetail = () => {
    setShowLeadDetailModal(false);
    setSelectedLead(null);
    setLeadAuditTrail([]);
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const [paymentsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/partner-portal/payments`, { headers: getAuthHeader() }),
        axios.get(`${API}/partner-portal/payments/summary`, { headers: getAuthHeader() })
      ]);
      setPayments(paymentsRes.data);
      setPaymentSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      toast.error('Failed to load payment records');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Public Link Management Functions
  const fetchPublicLink = async () => {
    setPublicLinkLoading(true);
    try {
      const response = await axios.get(`${API}/partner-portal/public-link`, {
        headers: getAuthHeader()
      });
      setPublicLink(response.data);
    } catch (error) {
      console.error('Failed to fetch public link:', error);
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const regeneratePublicLink = async () => {
    setPublicLinkLoading(true);
    try {
      const response = await axios.post(`${API}/partner-portal/public-link/regenerate`, {}, {
        headers: getAuthHeader()
      });
      setPublicLink(response.data);
      toast.success('Public link regenerated successfully');
    } catch (error) {
      console.error('Failed to regenerate public link:', error);
      toast.error('Failed to regenerate link');
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const togglePublicLink = async () => {
    try {
      const response = await axios.put(`${API}/partner-portal/public-link/toggle`, {}, {
        headers: getAuthHeader()
      });
      setPublicLink(prev => ({ ...prev, enabled: response.data.enabled }));
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to toggle public link:', error);
      toast.error('Failed to update link status');
    }
  };

  const copyPublicLink = () => {
    const fullUrl = `${window.location.origin}/partner/lead/${publicLink?.slug}`;
    navigator.clipboard.writeText(fullUrl);
    setPublicLinkCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setPublicLinkCopied(false), 2000);
  };

  // Document Management Functions
  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/partner-portal/documents`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadFormData.file || !uploadFormData.document_type) {
      toast.error('Please select a file and document type');
      return;
    }

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFormData.file);
      formData.append('document_type', uploadFormData.document_type);
      if (uploadFormData.document_name) {
        formData.append('document_name', uploadFormData.document_name);
      }

      await axios.post(`${API}/partner-portal/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadFormData({ document_type: '', document_name: '', file: null });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await axios.delete(`${API}/partner-portal/documents/${documentId}`, {
        headers: getAuthHeader()
      });
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/partner-portal/documents/${doc.id}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.document_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const getDocumentIcon = (mimeType) => {
    if (mimeType?.includes('image')) return <FileImage className="text-green-500" size={20} />;
    if (mimeType?.includes('pdf')) return <FileText className="text-red-500" size={20} />;
    return <File className="text-blue-500" size={20} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const documentTypes = [
    { value: 'pan_card', label: 'PAN Card' },
    { value: 'gst_certificate', label: 'GST Certificate' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'id_proof', label: 'ID Proof' },
    { value: 'company_registration', label: 'Company Registration' },
    { value: 'other', label: 'Other' }
  ];

  const getDocumentTypeLabel = (type) => {
    return documentTypes.find(d => d.value === type)?.label || type;
  };

  // Change Password Handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordFormData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API}/partner-portal/change-password`, {
        current_password: passwordFormData.current_password,
        new_password: passwordFormData.new_password
      }, {
        headers: getAuthHeader()
      });

      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${API}/schengen-countries`);
      // Extract just the country names from the response
      const countries = response.data.map(c => c.name).filter(Boolean);
      setSchengenCountries(countries);
    } catch (error) {
      console.error('Failed to fetch Schengen countries:', error);
      // Fallback to hardcoded list
      setSchengenCountries([
        'Austria', 'Belgium', 'Czech Republic', 'Denmark', 'Estonia', 'Finland',
        'France', 'Germany', 'Greece', 'Hungary', 'Iceland', 'Italy',
        'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands',
        'Norway', 'Poland', 'Portugal', 'Slovakia', 'Slovenia', 'Spain',
        'Sweden', 'Switzerland'
      ]);
    }
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    
    // Check if partner has accepted the agreement
    if (agreementStatus?.has_agreement && !agreementStatus?.accepted) {
      toast.error('You must accept the Partner Agreement before submitting leads. Please go to the Agreement tab.');
      setActiveTab('agreement');
      return;
    }
    
    setSubmittingLead(true);
    
    // Determine final country
    let finalCountry = leadFormData.destination_country;
    if (showSchengenDropdown && leadFormData.schengen_country) {
      finalCountry = leadFormData.schengen_country;
    } else if (leadFormData.destination_country === 'Other' && leadFormData.custom_country) {
      finalCountry = leadFormData.custom_country;
    }
    
    const submitData = {
      ...leadFormData,
      destination_country: finalCountry
    };
    
    try {
      const response = await axios.post(`${API}/partner-portal/leads`, submitData, {
        headers: getAuthHeader()
      });
      
      // Check if duplicate was found
      if (response.data.is_duplicate) {
        setDuplicateInfo(response.data.duplicate_info);
        setShowDuplicateModal(true);
        return;
      }
      
      toast.success('Lead submitted successfully!');
      setShowLeadForm(false);
      setShowSchengenDropdown(false);
      setLeadFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        destination_country: '',
        schengen_country: '',
        custom_country: '',
        travel_date: '',
        num_applicants: 1,
        message: ''
      });
      fetchDashboard();
      if (activeTab === 'leads') {
        fetchLeads();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit lead');
    } finally {
      setSubmittingLead(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/partner/login');
  };

  // Helper to open lead form with clean state
  const [showAgreementPendingModal, setShowAgreementPendingModal] = useState(false);

  const openLeadForm = () => {
    // Check if agreement is pending - show modal instead of form
    if (agreementStatus?.has_agreement && !agreementStatus?.accepted) {
      setShowAgreementPendingModal(true);
      return;
    }
    
    setLeadFormData({
      name: '',
      email: '',
      phone: '',
      service_type: '',
      destination_country: '',
      schengen_country: '',
      custom_country: '',
      travel_date: '',
      num_applicants: 1,
      message: ''
    });
    setShowSchengenDropdown(false);
    setShowLeadForm(true);
  };

  // Open bank modal with existing data
  const openBankModal = () => {
    if (partner?.bank_details) {
      setBankFormData({
        account_holder_name: partner.bank_details.account_holder_name || '',
        bank_name: partner.bank_details.bank_name || '',
        account_number: partner.bank_details.account_number || '',
        ifsc_code: partner.bank_details.ifsc_code || '',
        branch: partner.bank_details.branch || ''
      });
    } else {
      setBankFormData({
        account_holder_name: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        branch: ''
      });
    }
    setShowBankModal(true);
  };

  // Save bank details
  const handleSaveBankDetails = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      await axios.put(`${API}/partner-portal/bank-details`, bankFormData, {
        headers: getAuthHeader()
      });
      toast.success('Bank details saved successfully!');
      setShowBankModal(false);
      // Refresh partner data to show updated bank details
      await refreshPartner();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSavingBank(false);
    }
  };

  // View payment screenshot - fetch with auth and create blob URL
  const viewPaymentScreenshot = async (screenshotPath, commissionId) => {
    if (screenshotPath && commissionId) {
      try {
        const filename = screenshotPath.split('/').pop();
        const fullUrl = `${API}/partner-portal/files/commission-screenshot/${commissionId}/${filename}`;
        
        // Fetch with authentication
        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('partner_token')}`
          }
        });
        
        // Create blob URL for display
        const blobUrl = URL.createObjectURL(response.data);
        setSelectedScreenshot(blobUrl);
        setShowScreenshotModal(true);
      } catch (error) {
        console.error('Failed to load screenshot:', error);
        toast.error('Failed to load screenshot');
      }
    }
  };

  // Download/view payment receipt with auth
  const viewPaymentReceipt = async (receiptPath) => {
    if (receiptPath) {
      try {
        const filename = receiptPath.split('/').pop();
        const fullUrl = `${API}/partner-portal/files/payment-receipt/${filename}`;
        
        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('partner_token')}`
          }
        });
        
        // Create blob URL and open in new tab
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      } catch (error) {
        console.error('Failed to load receipt:', error);
        toast.error('Failed to load receipt');
      }
    }
  };

  // View payment screenshot (from payment records) with auth
  const viewPaymentRecordScreenshot = async (screenshotUrl) => {
    if (screenshotUrl) {
      try {
        const filename = screenshotUrl.split('/').pop();
        const fullUrl = `${API}/partner-portal/files/payment-screenshot/${filename}`;
        
        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('partner_token')}`
          }
        });
        
        const blobUrl = URL.createObjectURL(response.data);
        setSelectedScreenshot(blobUrl);
        setShowScreenshotModal(true);
      } catch (error) {
        console.error('Failed to load screenshot:', error);
        toast.error('Failed to load screenshot');
      }
    }
  };

  const getStageDisplay = (stage) => {
    const config = stageConfig.find(s => s.stage_key === stage);
    return config?.show_to_partner_as || config?.stage_label || stage;
  };

  const getStageBadgeColor = (stage) => {
    const stageColors = {
      new: 'bg-blue-100 text-blue-800',
      qualified: 'bg-yellow-100 text-yellow-800',
      proposal_sent: 'bg-purple-100 text-purple-800',
      agreement_signed: 'bg-indigo-100 text-indigo-800',
      invoiced: 'bg-orange-100 text-orange-800',
      welcome_call: 'bg-teal-100 text-teal-800',
      processing: 'bg-cyan-100 text-cyan-800',
      documents_collected: 'bg-lime-100 text-lime-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return stageColors[stage] || 'bg-gray-100 text-gray-800';
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'submit', label: 'Submit Lead', icon: UserPlus },
    { id: 'leads', label: 'My Leads', icon: FileText },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'commissions', label: 'Commissions', icon: IndianRupee },
    { id: 'agreement', label: 'Agreement', icon: FileSignature },
    { id: 'profile', label: 'Profile', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="animate-spin text-blue-600" size={40} />
          <p className="text-gray-600">Loading Partner Portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu size={24} />
          </button>
          <h1 className="font-bold">Partner Portal</h1>
        </div>
        <div className="flex items-center gap-3">
          <Bell size={20} />
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-700 to-purple-700 text-white transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-xl font-bold">Partner Portal</h1>
              <p className="text-sm text-blue-200">Visaasure</p>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X size={24} />
            </button>
          </div>

          {/* Partner Info */}
          <div className="bg-white/10 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Building2 size={20} />
              </div>
              <div>
                <p className="font-medium text-sm">{partner?.company_name}</p>
                <p className="text-xs text-blue-200">{partner?.contact_person}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    if (item.id === 'submit') {
                      openLeadForm();
                      setActiveTab('dashboard');
                    }
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-white text-blue-700 shadow-lg'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg transition-all"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 p-4 lg:p-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboardData && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Welcome, {partner?.contact_person}</h2>
              <p className="text-gray-600">Here&apos;s your partnership overview</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Total Leads</span>
                  <FileText className="text-blue-500" size={20} />
                </div>
                <p className="text-2xl font-bold">{dashboardData.stats?.total_leads || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Earned</span>
                  <IndianRupee className="text-green-500" size={20} />
                </div>
                <p className="text-2xl font-bold text-green-600">
                  ₹{(dashboardData.stats?.total_commission_earned || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Pending</span>
                  <Clock className="text-yellow-500" size={20} />
                </div>
                <p className="text-2xl font-bold text-yellow-600">
                  ₹{(dashboardData.stats?.pending_commission || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Paid Out</span>
                  <CheckCircle2 className="text-blue-500" size={20} />
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  ₹{(dashboardData.stats?.total_commission_paid || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Quick Submit Button */}
            <Button
              onClick={openLeadForm}
              className="w-full lg:w-auto mb-6 bg-gradient-to-r from-blue-600 to-purple-600"
            >
              <Plus size={20} className="mr-2" />
              Submit New Lead
            </Button>

            {/* Recent Leads */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold">Recent Leads</h3>
                <button
                  onClick={() => setActiveTab('leads')}
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  View All <ChevronRight size={16} />
                </button>
              </div>
              <div className="divide-y">
                {dashboardData.recent_leads?.length > 0 ? (
                  dashboardData.recent_leads.slice(0, 3).map((lead) => (
                    <div key={lead.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{lead.name}</p>
                        <p className="text-sm text-gray-500">{lead.service_type || 'No service'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(lead.created_at).toLocaleDateString('en-IN')} • {new Date(lead.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </p>
                      </div>
                      <Badge className={getStageBadgeColor(lead.stage)}>
                        {getStageDisplay(lead.stage)}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <FileText className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No leads yet. Submit your first lead!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">My Leads</h2>
              <Button onClick={openLeadForm}>
                <Plus size={18} className="mr-2" /> Submit Lead
              </Button>
            </div>

            {leadsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin text-blue-600" size={32} />
              </div>
            ) : leads.length > 0 ? (
              <div className="space-y-3">
                {leads.map((lead) => (
                  <div 
                    key={lead.id} 
                    className="bg-white rounded-xl p-4 shadow-sm border hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => openLeadDetail(lead)}
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{lead.name}</p>
                        <p className="text-sm text-gray-500">{lead.email} • {lead.phone}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {lead.service_type || 'No service'}
                          {lead.destination_country && ` • ${lead.destination_country}`}
                        </p>
                      </div>
                      <Badge className={getStageBadgeColor(lead.stage)}>
                        {lead.stage_display || getStageDisplay(lead.stage)}
                      </Badge>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-gray-400">
                      <span>ID: {lead.id}</span>
                      <div className="flex items-center gap-2">
                        <span>{new Date(lead.created_at).toLocaleDateString('en-IN')} • {new Date(lead.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                        <Eye size={14} className="text-blue-500" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 shadow-sm border text-center">
                <FileText className="mx-auto mb-4 text-gray-300" size={48} />
                <p className="text-gray-500 mb-4">No leads submitted yet</p>
                <Button onClick={openLeadForm}>
                  Submit Your First Lead
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Records</h2>

            {/* Payment Summary */}
            {paymentSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="text-blue-600" size={20} />
                    <span className="text-sm text-blue-700">Total Invoiced</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    ₹{paymentSummary.total_invoiced?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-blue-600">{paymentSummary.invoice_count || 0} invoices</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <IndianRupee className="text-green-600" size={20} />
                    <span className="text-sm text-green-700">Total Received</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    ₹{paymentSummary.total_received?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-green-600">{paymentSummary.paid_count || 0} fully paid</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="text-yellow-600" size={20} />
                    <span className="text-sm text-yellow-700">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-700">
                    ₹{paymentSummary.total_pending?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-yellow-600">{paymentSummary.partial_count || 0} partial</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="text-red-600" size={20} />
                    <span className="text-sm text-red-700">Cancelled</span>
                  </div>
                  <p className="text-2xl font-bold text-red-700">
                    ₹{paymentSummary.total_cancelled?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-red-600">{paymentSummary.cancelled_count || 0} cancelled</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="text-purple-600" size={20} />
                    <span className="text-sm text-purple-700">Refunded</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-700">
                    ₹{paymentSummary.total_refunded?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-purple-600">refund amount</p>
                </div>
              </div>
            )}

            {/* Payment Records List */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold">Invoice & Payment History</h3>
                <Button variant="outline" size="sm" onClick={fetchPayments} disabled={paymentsLoading}>
                  <RefreshCw size={14} className={`mr-2 ${paymentsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <div className="divide-y">
                {paymentsLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="animate-spin mx-auto mb-2 text-blue-600" size={32} />
                    <p className="text-gray-500">Loading payments...</p>
                  </div>
                ) : payments.length > 0 ? (
                  payments.map((payment, index) => (
                    <div key={payment.id || index} className={`p-4 ${payment.status === 'cancelled' ? 'bg-red-50' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{payment.lead_name}</p>
                          <p className="text-sm text-gray-500">
                            {payment.service_type}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Invoice: {payment.invoice_number}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${payment.status === 'cancelled' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                            ₹{payment.total_amount?.toLocaleString()}
                          </p>
                          <Badge className={
                            payment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            payment.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            payment.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {payment.status === 'cancelled' ? 'Cancelled' :
                             payment.payment_status === 'paid' ? 'Fully Paid' : 
                             payment.payment_status === 'partial' ? 'Partial' : 'Pending'}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Show cancellation details if cancelled */}
                      {payment.status === 'cancelled' && payment.cancellation && (
                        <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-200">
                          <p className="text-sm font-medium text-red-800">
                            <XCircle size={14} className="inline mr-1" />
                            Cancellation Details
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            <strong>Reason:</strong> {payment.cancellation.cancellation_reason || 'N/A'}
                          </p>
                          {payment.cancellation.cancellation_notes && (
                            <p className="text-sm text-red-700">
                              <strong>Notes:</strong> {payment.cancellation.cancellation_notes}
                            </p>
                          )}
                          <p className="text-xs text-red-600 mt-1">
                            Cancelled on {payment.cancellation.cancelled_at ? new Date(payment.cancellation.cancelled_at).toLocaleString('en-IN') : 'N/A'}
                          </p>
                          
                          {/* Show refund details if exists */}
                          {payment.refund && (
                            <div className="mt-2 pt-2 border-t border-red-300">
                              <p className="text-sm font-medium text-red-800">Refund Processed</p>
                              <p className="text-sm text-red-700">
                                <strong>Amount:</strong> ₹{payment.refund.refund_amount?.toLocaleString()} ({payment.refund.refund_type})
                              </p>
                              <p className="text-sm text-red-700">
                                <strong>Method:</strong> {payment.refund.refund_method}
                                {payment.refund.refund_reference && ` • Ref: ${payment.refund.refund_reference}`}
                              </p>
                            </div>
                          )}
                          
                          {/* Show commission reversal details if exists */}
                          {payment.commission_reversal && (
                            <div className="mt-2 pt-2 border-t border-red-300">
                              <p className="text-sm font-medium text-orange-800">
                                <AlertTriangle size={14} className="inline mr-1" />
                                Commission Impact
                              </p>
                              <p className="text-sm text-orange-700">
                                <strong>Reversal Amount:</strong> ₹{payment.commission_reversal.amount?.toLocaleString()}
                              </p>
                              <p className="text-sm text-orange-700">
                                <strong>Status:</strong>{' '}
                                <span className={`font-medium ${
                                  payment.commission_reversal.status === 'recovered' ? 'text-green-700' :
                                  payment.commission_reversal.status === 'pending_recovery' ? 'text-amber-700' :
                                  payment.commission_reversal.status === 'waived' ? 'text-gray-600' : 'text-orange-700'
                                }`}>
                                  {payment.commission_reversal.status === 'pending_recovery' ? 'Pending Recovery' :
                                   payment.commission_reversal.status === 'recovered' ? 'Recovered' :
                                   payment.commission_reversal.status === 'waived' ? 'Waived' :
                                   payment.commission_reversal.status}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Show payment details for non-cancelled invoices */}
                      {payment.status !== 'cancelled' && (
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Paid: ₹{payment.amount_paid?.toLocaleString()} • Due: ₹{payment.amount_due?.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-400">
                            {payment.issue_date && new Date(payment.issue_date).toLocaleDateString('en-IN')}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <CreditCard className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No payment records yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Payments will appear here once invoices are generated for your leads
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Commissions</h2>

            {/* Commission Summary */}
            {commissionSummary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="text-yellow-600" size={20} />
                    <span className="text-sm text-yellow-700">Pending Approval</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-700">
                    ₹{commissionSummary.pending?.amount?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-yellow-600">{commissionSummary.pending?.count || 0} records</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="text-blue-600" size={20} />
                    <span className="text-sm text-blue-700">Approved (Ready for Payout)</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    ₹{commissionSummary.approved?.amount?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-blue-600">{commissionSummary.approved?.count || 0} records</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <IndianRupee className="text-green-600" size={20} />
                    <span className="text-sm text-green-700">Paid Out</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    ₹{commissionSummary.paid?.amount?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-green-600">{commissionSummary.paid?.count || 0} records</p>
                </div>
              </div>
            )}

            {/* Commission Records */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Commission History</h3>
              </div>
              <div className="divide-y">
                {commissions.length > 0 ? (
                  commissions.map((comm) => (
                    <div key={comm.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{comm.lead_name}</p>
                          <p className="text-sm text-gray-500">{comm.service_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">₹{comm.commission_amount?.toLocaleString()}</p>
                          <Badge className={
                            comm.status === 'paid' ? 'bg-green-100 text-green-800' :
                            comm.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {comm.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-400">
                          {comm.commission_type === 'percentage' ? `${comm.commission_rate}%` : 'Flat'} on ₹{comm.base_amount?.toLocaleString()}
                        </p>
                        {comm.status === 'paid' && comm.payment_screenshot && (
                          <button
                            onClick={() => viewPaymentScreenshot(comm.payment_screenshot, comm.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Eye size={12} />
                            View Payment Proof
                          </button>
                        )}
                      </div>
                      {comm.status === 'paid' && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Paid on {new Date(comm.paid_at).toLocaleDateString()}
                            {comm.payment_reference && ` • Ref: ${comm.payment_reference}`}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <IndianRupee className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No commission records yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile</h2>
            
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Company Name</label>
                  <p className="font-medium">{partner?.company_name}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Contact Person</label>
                  <p className="font-medium">{partner?.contact_person}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Email</label>
                  <p className="font-medium">{partner?.email}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Phone</label>
                  <p className="font-medium">{partner?.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Status</label>
                  <Badge className="bg-green-100 text-green-800">{partner?.status}</Badge>
                </div>
              </div>
            </div>

            {/* Public Lead Link Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Globe size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Public Lead Submission Link</h3>
                    <p className="text-sm text-gray-500">Share this link with customers to collect leads directly</p>
                  </div>
                </div>
                {publicLink && (
                  <button
                    onClick={togglePublicLink}
                    className="flex items-center gap-2 text-sm"
                    data-testid="toggle-public-link-btn"
                  >
                    {publicLink.enabled ? (
                      <ToggleRight size={28} className="text-green-500" />
                    ) : (
                      <ToggleLeft size={28} className="text-gray-400" />
                    )}
                    <span className={publicLink.enabled ? 'text-green-600' : 'text-gray-500'}>
                      {publicLink.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </button>
                )}
              </div>

              {publicLinkLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="animate-spin text-blue-600" size={24} />
                </div>
              ) : publicLink ? (
                <div className="space-y-4">
                  {/* Link Display */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-50 border rounded-lg px-4 py-3 flex items-center gap-2 overflow-hidden">
                      <Link size={16} className="text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {window.location.origin}/partner/lead/{publicLink.slug}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyPublicLink}
                      className="flex-shrink-0"
                      data-testid="copy-public-link-btn"
                    >
                      {publicLinkCopied ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/partner/lead/${publicLink.slug}`, '_blank')}
                      className="flex-shrink-0"
                      data-testid="preview-public-link-btn"
                    >
                      <ExternalLink size={16} />
                    </Button>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <p className="text-xs text-gray-500">
                      Customers can use this link to submit their travel inquiries directly to you.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={regeneratePublicLink}
                      disabled={publicLinkLoading}
                      className="text-blue-600 hover:text-blue-700"
                      data-testid="regenerate-public-link-btn"
                    >
                      <RefreshCw size={14} className="mr-1" />
                      Regenerate Link
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Link size={32} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 mb-4">No public link generated yet</p>
                  <Button onClick={fetchPublicLink} data-testid="generate-public-link-btn">
                    <Link size={16} className="mr-2" />
                    Generate Public Link
                  </Button>
                </div>
              )}
            </div>

            {/* Security / Password Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Security</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowPasswordModal(true)}
                  data-testid="change-password-btn"
                >
                  <Lock size={16} className="mr-2" />
                  Change Password
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Keep your account secure by using a strong password. We recommend changing your password periodically.
              </p>
            </div>

            {/* Bank Account Details */}
            <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Bank Account Details</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={openBankModal}
                >
                  {partner?.bank_details?.account_number ? 'Update' : 'Add'} Bank Details
                </Button>
              </div>
              
              {partner?.bank_details?.account_number ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Account Holder Name</label>
                    <p className="font-medium">{partner.bank_details.account_holder_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Bank Name</label>
                    <p className="font-medium">{partner.bank_details.bank_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Account Number</label>
                    <p className="font-medium">
                      {partner.bank_details.account_number || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">IFSC Code</label>
                    <p className="font-medium">{partner.bank_details.ifsc_code || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">Branch</label>
                    <p className="font-medium">{partner.bank_details.branch || 'N/A'}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="mx-auto mb-2 opacity-50" size={32} />
                  <p>No bank details added yet</p>
                  <p className="text-sm">Add your bank details to receive commission payouts</p>
                </div>
              )}
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">My Documents</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowUploadModal(true)}
                  data-testid="upload-document-btn"
                >
                  <Upload size={16} className="mr-2" />
                  Upload Document
                </Button>
              </div>
              
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div 
                      key={doc.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      data-testid={`document-item-${doc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {getDocumentIcon(doc.mime_type)}
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{doc.file_name || doc.original_filename}</p>
                          <p className="text-xs text-gray-500">
                            {getDocumentTypeLabel(doc.document_type)} • {formatFileSize(doc.file_size)}
                            {doc.status && <span className={`ml-2 capitalize ${doc.status === 'approved' ? 'text-green-600' : doc.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'}`}>• {doc.status}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download size={16} />
                        </Button>
                        {/* Only show delete for non-approved documents */}
                        {doc.status !== 'approved' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="mx-auto mb-2 opacity-50" size={32} />
                  <p>No documents uploaded yet</p>
                  <p className="text-sm">Upload your business documents for verification</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agreement Tab */}
        {activeTab === 'agreement' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Partner Agreement</h2>
            
            {agreementStatus?.has_agreement ? (
              <div className="max-w-3xl">
                {agreementStatus.accepted ? (
                  /* Already Accepted View */
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="text-green-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Agreement Accepted</h3>
                        <p className="text-sm text-gray-500">
                          Version {agreementStatus.agreement_version} • Accepted on{' '}
                          {agreementStatus.acceptance_details?.accepted_at 
                            ? new Date(agreementStatus.acceptance_details.accepted_at).toLocaleString()
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="font-medium text-gray-900 mb-2">Acceptance Details</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Agreement ID:</span>
                          <span className="ml-2 font-mono text-xs">{agreementStatus.agreement_id?.slice(0, 8)}...</span>
                        </div>
                        <div>
                          <span className="text-gray-500">IP Address:</span>
                          <span className="ml-2 font-mono">{agreementStatus.acceptance_details?.ip_address || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          fetchAgreementContent();
                          setShowAgreementModal(true);
                        }}
                      >
                        <Eye size={16} className="mr-2" />
                        View Agreement
                      </Button>
                      <Button variant="outline" onClick={downloadAgreementPDF}>
                        <Download size={16} className="mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Need to Accept View */
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                        <AlertCircle className="text-yellow-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Agreement Pending</h3>
                        <p className="text-sm text-gray-500">
                          Please review and accept the partner agreement to continue
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-6">
                      Before you can submit leads and earn commissions, you need to read and accept 
                      the Business Partner Agreement (Version {agreementStatus.agreement_version}).
                    </p>

                    <Button 
                      onClick={() => {
                        fetchAgreementContent();
                        setHasScrolledToBottom(false);
                        setAgreementChecked(false);
                        setShowAgreementModal(true);
                      }}
                      className="w-full sm:w-auto"
                    >
                      <ScrollText size={16} className="mr-2" />
                      Read & Accept Agreement
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border p-8 text-center max-w-lg mx-auto">
                <FileSignature className="mx-auto mb-4 text-gray-300" size={48} />
                <h3 className="font-semibold text-gray-900 mb-2">No Agreement Available</h3>
                <p className="text-gray-500">
                  The partner agreement has not been configured yet. Please contact support.
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Lead Submission Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Submit New Lead</h3>
                <button onClick={() => setShowLeadForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmitLead} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <Input
                  value={leadFormData.name}
                  onChange={(e) => setLeadFormData({...leadFormData, name: e.target.value})}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    value={leadFormData.email}
                    onChange={(e) => setLeadFormData({...leadFormData, email: e.target.value})}
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <Input
                    value={leadFormData.phone}
                    onChange={(e) => setLeadFormData({...leadFormData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                <select
                  value={leadFormData.service_type}
                  onChange={(e) => setLeadFormData({...leadFormData, service_type: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.title}>{service.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination Country *</label>
                <select
                  value={leadFormData.destination_country}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setLeadFormData({...leadFormData, destination_country: value, schengen_country: '', custom_country: ''});
                    
                    // If Schengen is selected, fetch and show Schengen countries
                    if (value.toLowerCase() === 'schengen') {
                      await fetchSchengenCountries();
                      setShowSchengenDropdown(true);
                    } else {
                      setShowSchengenDropdown(false);
                    }
                  }}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select destination country</option>
                  <option value="USA">USA</option>
                  <option value="UK">UK</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Germany">Germany</option>
                  <option value="Schengen">Schengen (Europe)</option>
                  <option value="UAE">UAE</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Schengen Country Dropdown */}
              {showSchengenDropdown && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Schengen Country *</label>
                  <select
                    value={leadFormData.schengen_country}
                    onChange={(e) => setLeadFormData({...leadFormData, schengen_country: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select Schengen country</option>
                    {schengenCountries.map((country, index) => (
                      <option key={index} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Custom Country Input */}
              {leadFormData.destination_country === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Specify Country *</label>
                  <Input
                    value={leadFormData.custom_country}
                    onChange={(e) => setLeadFormData({...leadFormData, custom_country: e.target.value})}
                    placeholder="Enter country name"
                    required
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date</label>
                  <Input
                    type="date"
                    value={leadFormData.travel_date}
                    onChange={(e) => setLeadFormData({...leadFormData, travel_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No. of Applicants</label>
                  <Input
                    type="number"
                    min="1"
                    value={leadFormData.num_applicants}
                    onChange={(e) => setLeadFormData({...leadFormData, num_applicants: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  value={leadFormData.message}
                  onChange={(e) => setLeadFormData({...leadFormData, message: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Any additional information..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowLeadForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={submittingLead}>
                  {submittingLead ? 'Submitting...' : 'Submit Lead'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Lead Alert Modal */}
      {showDuplicateModal && duplicateInfo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-yellow-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Duplicate Lead Found</h3>
                <p className="text-sm text-gray-500">This lead already exists in our system</p>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Client Name:</span>
                  <span className="text-sm font-medium text-gray-900">{duplicateInfo.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Lead Source:</span>
                  <span className="text-sm font-medium text-gray-900">{duplicateInfo.source}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Stage:</span>
                  <Badge className={getStageBadgeColor(duplicateInfo.stage)}>
                    {getStageDisplay(duplicateInfo.stage)}
                  </Badge>
                </div>
                {duplicateInfo.created_at && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Created On:</span>
                    <span className="text-sm font-medium text-gray-900">{duplicateInfo.created_at}</span>
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              We already have this lead in our system. If you believe this is an error or have additional information, please contact our team.
            </p>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateInfo(null);
                }}
                className="flex-1"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateInfo(null);
                  setShowLeadForm(false);
                  setShowSchengenDropdown(false);
                  // Clear the form
                  setLeadFormData({
                    name: '',
                    email: '',
                    phone: '',
                    service_type: '',
                    destination_country: '',
                    schengen_country: '',
                    custom_country: '',
                    travel_date: '',
                    num_applicants: 1,
                    message: ''
                  });
                }}
                className="flex-1"
              >
                Got It
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
        <div className="flex justify-around py-2">
          {menuItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'submit') {
                    openLeadForm();
                  } else {
                    setActiveTab(item.id);
                  }
                }}
                className={`flex flex-col items-center py-2 px-3 rounded-lg ${
                  isActive ? 'text-blue-600' : 'text-gray-400'
                }`}
              >
                <Icon size={20} />
                <span className="text-xs mt-1">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Bank Details Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Bank Account Details</h3>
                <button onClick={() => setShowBankModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">Add your bank details for commission payouts</p>
            </div>
            <form onSubmit={handleSaveBankDetails} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name *</label>
                <input
                  type="text"
                  value={bankFormData.account_holder_name}
                  onChange={(e) => setBankFormData({...bankFormData, account_holder_name: e.target.value})}
                  placeholder="Name as per bank account"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                <input
                  type="text"
                  value={bankFormData.bank_name}
                  onChange={(e) => setBankFormData({...bankFormData, bank_name: e.target.value})}
                  placeholder="e.g., HDFC Bank"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                <input
                  type="text"
                  value={bankFormData.account_number}
                  onChange={(e) => setBankFormData({...bankFormData, account_number: e.target.value})}
                  placeholder="Enter account number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code *</label>
                <input
                  type="text"
                  value={bankFormData.ifsc_code}
                  onChange={(e) => setBankFormData({...bankFormData, ifsc_code: e.target.value.toUpperCase()})}
                  placeholder="e.g., HDFC0001234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <input
                  type="text"
                  value={bankFormData.branch}
                  onChange={(e) => setBankFormData({...bankFormData, branch: e.target.value})}
                  placeholder="Branch name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowBankModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingBank} className="flex-1">
                  {savingBank ? 'Saving...' : 'Save Details'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Screenshot Modal */}
      {showScreenshotModal && selectedScreenshot && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => {
          // Revoke blob URL to prevent memory leak
          if (selectedScreenshot && selectedScreenshot.startsWith('blob:')) {
            URL.revokeObjectURL(selectedScreenshot);
          }
          setShowScreenshotModal(false);
          setSelectedScreenshot(null);
        }}>
          <div className="relative max-w-3xl max-h-[90vh]">
            <button 
              onClick={() => {
                if (selectedScreenshot && selectedScreenshot.startsWith('blob:')) {
                  URL.revokeObjectURL(selectedScreenshot);
                }
                setShowScreenshotModal(false);
                setSelectedScreenshot(null);
              }}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X size={32} />
            </button>
            <img 
              src={selectedScreenshot} 
              alt="Payment Screenshot" 
              className="max-w-full max-h-[85vh] rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Agreement Modal */}
      {showAgreementModal && agreementContent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{agreementContent.title}</h3>
                <p className="text-sm text-gray-500">Version {agreementContent.version}</p>
              </div>
              <button 
                onClick={() => setShowAgreementModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div 
              ref={agreementContentRef}
              className="flex-1 overflow-y-auto p-6"
              onScroll={handleAgreementScroll}
            >
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: agreementContent.content }}
              />
              
              {/* Scroll indicator */}
              {!hasScrolledToBottom && !agreementContent.accepted && (
                <div className="sticky bottom-0 bg-gradient-to-t from-white pt-8 pb-2 text-center">
                  <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                    <ChevronRight className="rotate-90 animate-bounce" size={16} />
                    Scroll down to read the full agreement
                  </p>
                </div>
              )}
            </div>

            {/* Footer with acceptance controls */}
            {!agreementContent.accepted && (
              <div className="p-6 border-t bg-gray-50 rounded-b-2xl flex-shrink-0">
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="agreementCheckbox"
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    disabled={!hasScrolledToBottom}
                    className="mt-1 w-5 h-5"
                  />
                  <label 
                    htmlFor="agreementCheckbox" 
                    className={`text-sm ${hasScrolledToBottom ? 'text-gray-700' : 'text-gray-400'}`}
                  >
                    I have read and understood the entire agreement and agree to be bound by its terms and conditions.
                  </label>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAgreementModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAcceptAgreement}
                    disabled={!hasScrolledToBottom || !agreementChecked || acceptingAgreement}
                    className="flex-1"
                  >
                    {acceptingAgreement ? 'Processing...' : (
                      <>
                        <Check size={16} className="mr-2" />
                        Accept Agreement
                      </>
                    )}
                  </Button>
                </div>

                {!hasScrolledToBottom && (
                  <p className="text-xs text-amber-600 mt-3 text-center">
                    Please scroll to the bottom of the agreement to enable acceptance
                  </p>
                )}
              </div>
            )}

            {/* Already accepted footer */}
            {agreementContent.accepted && (
              <div className="p-6 border-t bg-green-50 rounded-b-2xl flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 size={20} />
                    <span className="font-medium">Agreement Accepted</span>
                    <span className="text-sm text-green-600">
                      on {agreementContent.acceptance_details?.accepted_at 
                        ? new Date(agreementContent.acceptance_details.accepted_at).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  <Button variant="outline" onClick={() => setShowAgreementModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Upload Document</h3>
                <button 
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFormData({ document_type: '', document_name: '', file: null });
                  }} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <form onSubmit={handleDocumentUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                <select
                  value={uploadFormData.document_type}
                  onChange={(e) => setUploadFormData({...uploadFormData, document_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  data-testid="document-type-select"
                >
                  <option value="">Select document type</option>
                  {documentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Name (Optional)</label>
                <Input
                  value={uploadFormData.document_name}
                  onChange={(e) => setUploadFormData({...uploadFormData, document_name: e.target.value})}
                  placeholder="e.g., PAN Card - Company"
                  data-testid="document-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => setUploadFormData({...uploadFormData, file: e.target.files[0]})}
                    className="hidden"
                    id="document-file-input"
                    data-testid="document-file-input"
                  />
                  <label htmlFor="document-file-input" className="cursor-pointer">
                    {uploadFormData.file ? (
                      <div className="flex items-center justify-center gap-2 text-blue-600">
                        <FileText size={20} />
                        <span className="text-sm font-medium">{uploadFormData.file.name}</span>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <Upload className="mx-auto mb-2" size={24} />
                        <p className="text-sm">Click to select a file</p>
                        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC (max 5MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFormData({ document_type: '', document_name: '', file: null });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={uploadingDocument || !uploadFormData.file || !uploadFormData.document_type}
                  className="flex-1"
                  data-testid="submit-upload-btn"
                >
                  {uploadingDocument ? 'Uploading...' : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <KeyRound className="text-blue-600" size={20} />
                  </div>
                  <h3 className="text-xl font-bold">Change Password</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordFormData({ current_password: '', new_password: '', confirm_password: '' });
                  }} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password *</label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordFormData.current_password}
                    onChange={(e) => setPasswordFormData({...passwordFormData, current_password: e.target.value})}
                    placeholder="Enter your current password"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordFormData.new_password}
                    onChange={(e) => setPasswordFormData({...passwordFormData, new_password: e.target.value})}
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password *</label>
                <Input
                  type="password"
                  value={passwordFormData.confirm_password}
                  onChange={(e) => setPasswordFormData({...passwordFormData, confirm_password: e.target.value})}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
                {passwordFormData.new_password && passwordFormData.confirm_password && 
                  passwordFormData.new_password !== passwordFormData.confirm_password && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordFormData({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={changingPassword || !passwordFormData.current_password || !passwordFormData.new_password || 
                    passwordFormData.new_password !== passwordFormData.confirm_password}
                  className="flex-1"
                  data-testid="submit-password-change-btn"
                >
                  {changingPassword ? 'Changing...' : (
                    <>
                      <Lock size={16} className="mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agreement Pending Modal */}
      {showAgreementPendingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <FileSignature className="text-amber-600" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
                Agreement Acceptance Required
              </h3>
              <p className="text-center text-gray-600 mb-6">
                Before you can submit leads, you need to review and accept our Partner Agreement. 
                This ensures both parties are aligned on terms and conditions.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Action Required</p>
                    <p>Please go to the Agreement tab, carefully read the terms, and accept to continue submitting leads.</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAgreementPendingModal(false)}
                  className="flex-1"
                >
                  Later
                </Button>
                <Button 
                  onClick={() => {
                    setShowAgreementPendingModal(false);
                    setActiveTab('agreement');
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <FileSignature size={16} className="mr-2" />
                  View Agreement
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal with Audit Trail */}
      {showLeadDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Lead Details</h3>
                <p className="text-sm text-gray-500">ID: {selectedLead.id}</p>
              </div>
              <button
                onClick={closeLeadDetail}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="close-lead-detail-btn"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Lead Info Section */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <UserPlus size={18} />
                  Lead Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-medium">{selectedLead.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Phone</p>
                    <p className="font-medium">{selectedLead.phone}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Service</p>
                    <p className="font-medium">{selectedLead.service_type || 'Not specified'}</p>
                  </div>
                  {selectedLead.destination_country && (
                    <div>
                      <p className="text-gray-500">Destination</p>
                      <p className="font-medium">{selectedLead.destination_country}</p>
                    </div>
                  )}
                  {selectedLead.travel_date && (
                    <div>
                      <p className="text-gray-500">Travel Date</p>
                      <p className="font-medium">{new Date(selectedLead.travel_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Current Status</p>
                    <Badge className={getStageBadgeColor(selectedLead.stage)}>
                      {selectedLead.stage_display || getStageDisplay(selectedLead.stage)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Submitted On</p>
                    <p className="font-medium">{new Date(selectedLead.created_at).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              {/* Audit Trail Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ScrollText size={18} />
                  Activity Timeline
                </h4>
                
                {loadingAuditTrail ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="animate-spin text-blue-600" size={24} />
                    <span className="ml-2 text-gray-500">Loading activity...</span>
                  </div>
                ) : leadAuditTrail.length > 0 ? (
                  <div className="space-y-3">
                    {leadAuditTrail.map((entry, index) => (
                      <div 
                        key={index}
                        className="relative pl-6 pb-3 border-l-2 border-gray-200 last:border-transparent"
                      >
                        {/* Timeline dot */}
                        <div className="absolute left-[-5px] top-1 w-2 h-2 rounded-full bg-blue-500"></div>
                        
                        <div className="bg-white border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900 text-sm">{entry.type}</span>
                            <span className="text-xs text-gray-400">
                              {entry.created_at ? new Date(entry.created_at).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : ''}
                            </span>
                          </div>
                          {entry.stage && (
                            <Badge className={`${getStageBadgeColor(entry.stage)} text-xs mb-1`}>
                              {entry.stage}
                            </Badge>
                          )}
                          {entry.description && (
                            <p className="text-sm text-gray-600 mt-1">{entry.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Clock size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No activity recorded yet</p>
                  </div>
                )}
              </div>

              {/* Stage History from Lead (backup display) */}
              {selectedLead.stage_history && selectedLead.stage_history.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <TrendingUp size={18} />
                    Stage Progress
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedLead.stage_history.map((history, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Badge className="bg-gray-100 text-gray-700">
                          {history.stage}
                        </Badge>
                        {index < selectedLead.stage_history.length - 1 && (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50">
              <Button 
                onClick={closeLeadDetail}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerPortal;
